import React from "react";
import { hexToRgbString, addOpacityToRgb } from "@/utils/colorUtils";
import { cn, formatNumber } from "@/lib/utils";
import { Project } from "@/types";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Language } from "@/contexts/LanguageContext";
import getProjectTranslation from "@/utils/getProjectTranslation";

interface ProjectPinProps {
  project: Project;
  onClick?: () => void;
  isSelected?: boolean;
  shouldPulse?: boolean;
  customColor?: string;
  language: Language;
  t: (key: string) => string;
}

const ProjectPin: React.FC<ProjectPinProps> = ({
  project,
  onClick,
  isSelected = false,
  shouldPulse = false,
  customColor,
  language,
  t,
}) => {
  // Use custom color if provided, otherwise use status colors
  const getStatusColor = () => {
    if (customColor) {
      return customColor;
    }

    return isSelected ? "#10B981" : "#6B7280";
  };

  // Create custom pulse animation based on customer color
  React.useEffect(() => {
    if (customColor && (isSelected || shouldPulse)) {
      const color = customColor.startsWith("#")
        ? hexToRgbString(customColor)
        : customColor;

      const style = document.createElement("style");
      style.textContent = `
        @keyframes custom-pin-pulse {
          0% {
            box-shadow: 0 0 0 0 ${addOpacityToRgb(color, 0.7)};
          }
          70% {
            box-shadow: 0 0 0 10px ${addOpacityToRgb(color, 0)};
          }
          100% {
            box-shadow: 0 0 0 0 ${addOpacityToRgb(color, 0)};
          }
        }

        .custom-pin-pulse {
          animation: custom-pin-pulse 1.5s infinite;
        }
      `;

      document.head.appendChild(style);

      return () => {
        document.head.removeChild(style);
      };
    }
  }, [customColor, isSelected, shouldPulse]);

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  // Determine pulse class based on custom color availability
  const pulseClass = customColor
    ? isSelected || shouldPulse
      ? "custom-pin-pulse"
      : ""
    : isSelected || shouldPulse
      ? "pin-pulse"
      : "";

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className={cn(
            "relative cursor-pointer transition-all duration-300 transform",
            isSelected ? "scale-125 z-50" : "hover:scale-110",
          )}
          onClick={handleClick}
        >
          <div
            className={cn(
              "w-5 h-5 rounded-full border-2 border-white flex items-center justify-center",
              pulseClass,
            )}
            style={{ backgroundColor: getStatusColor() }}
          />
        </div>
      </TooltipTrigger>
      <TooltipContent
        className="bg-white border border-gray-200 p-4 rounded-lg text-xs max-w-[320px]"
        sideOffset={5}
      >
        <div className="space-y-2">
          <h3 className="font-medium text-sm max-w-[288px] break-words">
            {getProjectTranslation(project.nameTranslations, language) ||
              project.title}
          </h3>

          <div className="flex items-center gap-2 pt-2">
            <div className="flex flex-col">
              <span className="text-[10px] text-gray-500">
                {t("projectDetail.area")}
              </span>
              <span className="text-xs font-medium">
                {project.area
                  ? formatNumber(project.area / 10000, language) + " ha"
                  : "N/A"}
              </span>
            </div>

            {project.forecastedAmountYearly && (
              <div className="flex flex-col">
                <span className="text-[10px] text-gray-500">
                  {t("projectDetail.carbonStoredPerYear")}
                </span>
                <span className="text-xs font-medium">
                  {formatNumber(
                    project.forecastedAmountYearly / 1000,
                    language,
                  )}{" "}
                  t/year
                </span>
              </div>
            )}
          </div>

          <div className="text-[10px] text-gray-500 pt-1">
            {t("projectDetail.clickForDetails")}
          </div>
        </div>
      </TooltipContent>
    </Tooltip>
  );
};

export default ProjectPin;
